import { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton,
  FormControl, InputLabel, Select, MenuItem, Divider,
  useMediaQuery, useTheme, Chip
} from '@mui/material';
import { Close, Add, Delete, ShoppingCart, Warning, CheckCircle } from '@mui/icons-material';

const SaleOrderModal = ({
  open,
  onClose,
  modalAction,
  formData,
  setFormData,
  SaleOrderDetails,
  setSaleOrderDetails,
  onSave,
  onVerify,
  customers,
  items,
  loading,
  freightPackingGST
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isViewMode = modalAction === 'view';
  const title = modalAction === 'create' ? 'Create Sale Order' : modalAction === 'edit' ? 'Edit Sale Order' : 'View Sale Order';

  // Data loss warning state
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [originalFormData, setOriginalFormData] = useState({});
  const [originalSaleOrderDetails, setOriginalSaleOrderDetails] = useState([]);

  // Verify confirmation modal state
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);

  // Initialize original data when modal opens
  useEffect(() => {
    if (open && !isViewMode) {
      setOriginalFormData({ ...formData });
      setOriginalSaleOrderDetails([...SaleOrderDetails]);
      setHasUnsavedChanges(false);
    }
  }, [open, modalAction]);

  // Track changes in form data
  useEffect(() => {
    if (!isViewMode && Object.keys(originalFormData).length > 0) {
      const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalFormData);
      const hasDetailChanges = JSON.stringify(SaleOrderDetails) !== JSON.stringify(originalSaleOrderDetails);
      setHasUnsavedChanges(hasFormChanges || hasDetailChanges);
    }
  }, [formData, SaleOrderDetails, originalFormData, originalSaleOrderDetails, isViewMode]);

  // Handle close with unsaved changes check
  const handleCloseClick = () => {
    if (hasUnsavedChanges && !isViewMode) {
      setCancelConfirmOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setHasUnsavedChanges(false);
    onClose();
  };

  const handleStayOnPage = () => {
    setCancelConfirmOpen(false);
  };

  // Verify handlers
  const handleVerifyClick = () => {
    setVerifyConfirmOpen(true);
  };

  const handleVerifyConfirm = async () => {
    setVerifyConfirmOpen(false);
    if (onVerify) {
      await onVerify(formData.Id, formData.SaleOrderNo);
    }
  };

  const handleVerifyCancel = () => {
    setVerifyConfirmOpen(false);
  };

  // Enhanced save handler that clears unsaved changes flag
  const handleSave = async () => {
    try {
      await onSave();
      setHasUnsavedChanges(false);
      setOriginalFormData({ ...formData });
      setOriginalSaleOrderDetails([...SaleOrderDetails]);
    } catch (error) {
      // Keep unsaved changes flag if save fails
      console.error('Save failed:', error);
    }
  };
  
  // Helper functions to get customer details
  const getSelectedCustomerGST = () => {
    if (!formData.MasterId) return null;
    const customer = customers.find(c => (c.MasterId || c.Id) === parseInt(formData.MasterId));
    return customer?.GSTNumber || customer?.GSTNo || customer?.GSTIN || null;
  };

  const getSelectedCustomerInvoiceType = () => {
    if (!formData.MasterId) return null;
    const customer = customers.find(c => (c.MasterId || c.Id) === parseInt(formData.MasterId));
    return customer?.InvoiceTypeName || customer?.InvoiceType || null;
  };

  // Helper function to check if IGST should be used based on InvoiceTypeName
  const isIGSTInvoice = () => {
    if (!formData.MasterId) return false;
    const customer = customers.find(c => (c.MasterId || c.Id) === parseInt(formData.MasterId));
    const invoiceType = (customer?.InvoiceTypeName || customer?.InvoiceType || '').toLowerCase();
    return invoiceType.includes('igst');
  };

  const customerGSTNo = getSelectedCustomerGST();
  const customerInvoiceType = getSelectedCustomerInvoiceType();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // If customer changes, recalculate totals to apply correct GST type
    if (field === 'MasterId') {
      // Small delay to ensure state is updated before recalculation
      setTimeout(() => {
        calculateTotals(SaleOrderDetails);
      }, 100);
    }
  };

  const handleDetailChange = (index, field, value) => {
    const updatedDetails = [...SaleOrderDetails];
    updatedDetails[index] = { ...updatedDetails[index], [field]: value };
    
    if (['Quantity', 'Rate', 'DiscountPercentage', 'GSTRate'].includes(field)) {
      const detail = updatedDetails[index];
      const quantity = parseFloat(detail.Quantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const discountPct = parseFloat(detail.DiscountPercentage) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;
      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const taxableAmount = amount - discountAmount;
      const gstAmount = (taxableAmount * gstRate) / 100;
      const currentUseIGST = isIGSTInvoice(); // Get current GST type
      updatedDetails[index] = {
        ...updatedDetails[index],
        Amount: amount, DiscountAmount: discountAmount, TaxableAmount: taxableAmount,
        CGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
        SGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
        IGSTAmount: currentUseIGST ? gstAmount : 0, 
        TotalAmount: taxableAmount + gstAmount
      };
    }
    setSaleOrderDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const handleItemSelect = (index, itemId) => {
    const selectedItem = items.find(i => i.Id === parseInt(itemId));
    if (selectedItem) {
      const updatedDetails = [...SaleOrderDetails];
      const quantity = parseFloat(updatedDetails[index].Quantity) || 0;
      const rate = selectedItem.SaleRate || selectedItem.PurchaseRate || 0;
      const gstRate = selectedItem.GSTRate || 0;
      const discountPct = parseFloat(updatedDetails[index].DiscountPercentage) || 0;
      const amount = quantity * rate;
      const discountAmount = (amount * discountPct) / 100;
      const taxableAmount = amount - discountAmount;
      const gstAmount = (taxableAmount * gstRate) / 100;
      const currentUseIGST = isIGSTInvoice(); // Get current GST type
      updatedDetails[index] = {
        ...updatedDetails[index], ItemId: itemId, HSNCode: selectedItem.HSNCode || '', GSTRate: gstRate, Rate: rate,
        Amount: amount, DiscountAmount: discountAmount, TaxableAmount: taxableAmount,
        CGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
        SGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
        IGSTAmount: currentUseIGST ? gstAmount : 0, 
        TotalAmount: taxableAmount + gstAmount
      };
      setSaleOrderDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  };

  const addDetailRow = () => {
    setSaleOrderDetails([...SaleOrderDetails, {
      Id: 0, ItemId: '', HSNCode: '', GSTRate: 0, Quantity: 0, Rate: 0, Amount: 0,
      DiscountPercentage: 0, DiscountAmount: 0, TaxableAmount: 0, 
      CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0, IsActive: true
    }]);
  };

  const removeDetailRow = (index) => {
    const updatedDetails = SaleOrderDetails.filter((_, i) => i !== index);
    setSaleOrderDetails(updatedDetails);
    calculateTotals(updatedDetails);
  };

  const calculateTotals = (details) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    // Get default GST rates from API
    const defaultFreightGSTRate = parseFloat(freightPackingGST?.freight?.GSTRate || freightPackingGST?.freight?.GstRate) || 0;
    const defaultPackingGSTRate = parseFloat(freightPackingGST?.packing?.GSTRate || freightPackingGST?.packing?.GstRate) || 0;
    
    // Find the highest GST rate from items
    const maxItemGSTRate = details.length > 0 
      ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0))
      : 0;
    
    // Use higher of default or max item GST rate (never downgrade from default)
    const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

    const freightAmount = parseFloat(formData.FreightAmount) || 0;
    const packingAmount = parseFloat(formData.PackingAmount) || 0;
    
    // Calculate freight and packing GST as single amounts (like Purchase Order system)
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;
    
    // totals already contains line item GST amounts - don't modify them
    // Freight and packing GST are added separately to grand total
    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount;

    setFormData(prev => ({
      ...prev, 
      ...totals, 
      FreightGSTRate: effectiveFreightGSTRate,
      FreightGSTAmount: freightGSTAmount, 
      PackingGSTRate: effectivePackingGSTRate,
      PackingGSTAmount: packingGSTAmount,
      RoundOff: Math.round(grandTotal) - grandTotal, 
      TotalAmount: Math.round(grandTotal)
    }));
  };

  // Recalculate when freight/packing amounts or GST rates change
  useEffect(() => {
    calculateTotals(SaleOrderDetails);
  }, [formData.FreightAmount, formData.FreightGSTRate, formData.PackingAmount, formData.PackingGSTRate]);

  // Recalculate all items when Customer changes (affects GST type)
  useEffect(() => {
    if (formData.MasterId && SaleOrderDetails.length > 0) {
      // Recalculate all line items with new GST type
      const currentUseIGST = isIGSTInvoice();
      const updatedDetails = SaleOrderDetails.map(detail => {
        const gstRate = parseFloat(detail.GSTRate) || 0;
        const taxableAmount = parseFloat(detail.TaxableAmount) || 0;
        const gstAmount = (taxableAmount * gstRate) / 100;
        
        return {
          ...detail,
          CGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
          SGSTAmount: currentUseIGST ? 0 : gstAmount / 2, 
          IGSTAmount: currentUseIGST ? gstAmount : 0, 
          TotalAmount: taxableAmount + gstAmount
        };
      });
      
      setSaleOrderDetails(updatedDetails);
      calculateTotals(updatedDetails);
    }
  }, [formData.MasterId]);

  const formatCurrency = (amt) => `₹${(amt || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const disabledFieldStyle = {
    '& .MuiInputBase-input.Mui-disabled': {
      color: '#5D4037',
      fontWeight: 700,
      WebkitTextFillColor: '#5D4037'
    }
  };
  
  const compactInput = { 
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem' }, 
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && disabledFieldStyle)
  };
  const compactInputRight = { 
    '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right', fontWeight: 700 }, 
    '& .MuiInputLabel-root': { fontSize: '0.8rem' },
    ...(isViewMode && disabledFieldStyle)
  };
  const tinyInput = { 
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.7rem', textAlign: 'right', fontWeight: 700 }, 
    minWidth: 0,
    ...(isViewMode && disabledFieldStyle)
  };
  const tinyInputCenter = { 
    '& .MuiInputBase-input': { py: 0.5, px: 0.5, fontSize: '0.7rem', textAlign: 'center' }, 
    minWidth: 0,
    ...(isViewMode && disabledFieldStyle)
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or escape key if there are unsaved changes
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            if (hasUnsavedChanges && !isViewMode) {
              setCancelConfirmOpen(true);
              return;
            }
          }
          onClose();
        }} 
        maxWidth="lg" 
        fullWidth 
        fullScreen={isMobile}
        disableEscapeKeyDown={hasUnsavedChanges && !isViewMode}
        slotProps={{
          backdrop: {
            onClick: (e) => {
              if (hasUnsavedChanges && !isViewMode) {
                e.stopPropagation();
                setCancelConfirmOpen(true);
              }
            }
          }
        }}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : '10px', m: isMobile ? 0 : 2 } }}
      >
        
        <DialogTitle sx={{ py: 1, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#1e40af', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1e293b', fontSize: isMobile ? '0.9rem' : '1rem' }}>{title}</Typography>
              {hasUnsavedChanges && !isViewMode && (
                <Chip 
                  label="Unsaved Changes"
                  size="small"
                  sx={{ 
                    fontSize: '0.65rem', 
                    height: '20px', 
                    bgcolor: '#fef3c7', 
                    color: '#92400e',
                    border: '1px solid #f59e0b',
                    ml: 1
                  }}
                />
              )}
            </Box>
            <IconButton onClick={handleCloseClick} size="small" sx={{ p: 0.5 }}><Close fontSize="small" /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 1.5 : 2, bgcolor: '#fff' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 1, display: 'block' }}>Sale Order Information</Typography>
          <Grid container spacing={1} sx={{ mb: 1.5 }}>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="Sale Order No" value={formData.SaleOrderNo || ''} 
                disabled={true} sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: '#f0f9ff' } }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="date" label="Sale Order Date" value={formData.SaleOrderDate || ''} 
                onChange={(e) => handleChange('SaleOrderDate', e.target.value)} disabled={isViewMode} 
                InputLabelProps={{ shrink: true }} sx={compactInput} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small" sx={compactInput}>
                <InputLabel>Customer</InputLabel>
                <Select value={formData.MasterId || ''} onChange={(e) => handleChange('MasterId', e.target.value)} 
                  disabled={isViewMode} label="Customer" sx={{ fontSize: '0.8rem' }}
                  MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}>
                  {customers.map(c => <MenuItem key={c.MasterId || c.Id} value={c.MasterId || c.Id} sx={{ fontSize: '0.75rem', py: 0.5 }}>{c.Name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="GST No" value={customerGSTNo || '-'} disabled 
                sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: customerGSTNo ? '#f0fdf4' : '#f9fafb' } }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" label="Invoice Type" value={customerInvoiceType || '-'} disabled 
                sx={{ ...compactInput, ...disabledFieldStyle, '& .MuiInputBase-root': { bgcolor: customerInvoiceType ? '#fef3c7' : '#f9fafb' } }} />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af' }}>Sale Order Items</Typography>
            {!isViewMode && (
              <Button size="small" startIcon={<Add sx={{ fontSize: 16 }} />} onClick={addDetailRow} 
                variant="outlined" sx={{ py: 0.25, px: 1, fontSize: '0.7rem', minHeight: 0 }}>Add</Button>
            )}
          </Box>

          {isMobile ? (
          <Box sx={{ maxHeight: 300, overflow: 'auto', mb: 1.5 }}>
            {SaleOrderDetails.map((detail, index) => (
              <Box key={index} sx={{ 
                border: '1px solid #d1d5db', 
                borderRadius: 2, 
                p: 1.5, 
                mb: 1.5, 
                bgcolor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
              }}>
                {/* Header with Item number and Delete */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ 
                      bgcolor: '#1e40af', 
                      color: '#fff', 
                      borderRadius: '50%', 
                      width: 22, 
                      height: 22, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      fontSize: '0.7rem',
                      fontWeight: 600
                    }}>
                      {index + 1}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#374151', fontSize: '0.8rem' }}>
                      {items.find(i => i.Id === parseInt(detail.ItemId))?.Name || 'Select Item'}
                    </Typography>
                  </Box>
                  {!isViewMode && (
                    <IconButton size="small" onClick={() => removeDetailRow(index)} 
                      sx={{ p: 0.5, bgcolor: '#fef2f2', color: '#dc2626', '&:hover': { bgcolor: '#fee2e2' } }}>
                      <Delete sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>

                {/* Item Select */}
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel sx={{ fontSize: '0.75rem' }}>Select Item</InputLabel>
                  <Select 
                    value={detail.ItemId || ''} 
                    onChange={(e) => handleItemSelect(index, e.target.value)} 
                    disabled={isViewMode} 
                    label="Select Item"
                    sx={{ fontSize: '0.8rem', '& .MuiSelect-select': { py: 1 } }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: '0.75rem' }}>Select Item</MenuItem>
                    {items.map(item => <MenuItem key={item.Id} value={item.Id} sx={{ fontSize: '0.75rem', py: 0.5 }}>{item.Name}</MenuItem>)}
                  </Select>
                </FormControl>

                {/* Row 1: HSN, GST%, Qty */}
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>HSN Code</Typography>
                    <TextField 
                      size="small" 
                      value={detail.HSNCode || ''} 
                      onChange={(e) => handleDetailChange(index, 'HSNCode', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="-"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>GST %</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.GSTRate || ''} 
                      onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} 
                      disabled
                      fullWidth 
                      placeholder="0"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center' }, ...disabledFieldStyle }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Quantity</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.Quantity || ''} 
                      onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="0"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'center', fontWeight: 500 }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                </Box>

                {/* Row 2: Rate, Disc%, Total */}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Rate (₹)</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.Rate || ''} 
                      onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="0.00"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right', fontWeight: 700 }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Disc %</Typography>
                    <TextField 
                      size="small" 
                      type="number" 
                      value={detail.DiscountPercentage || ''} 
                      onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)} 
                      disabled={isViewMode} 
                      fullWidth 
                      placeholder="0"
                      sx={{ '& .MuiInputBase-input': { py: 0.75, fontSize: '0.8rem', textAlign: 'right', fontWeight: 700 }, ...(isViewMode && disabledFieldStyle) }} 
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.65rem', display: 'block', mb: 0.25 }}>Total</Typography>
                    <Box sx={{ 
                      bgcolor: '#dbeafe', 
                      borderRadius: 1, 
                      py: 0.75, 
                      px: 0.5,
                      textAlign: 'center',
                      border: '1px solid #93c5fd'
                    }}>
                      <Typography sx={{ fontSize: '0.8rem', color: '#1e40af', fontWeight: 800 }}>
                        {formatCurrency(detail.TotalAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
            {SaleOrderDetails.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: '#6b7280', bgcolor: '#f9fafb', borderRadius: 2, border: '1px dashed #d1d5db' }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>No items added</Typography>
                <Typography variant="caption">Tap "+ Add" button to add items</Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, mb: 1.5, overflow: 'hidden' }}>
            <Box sx={{ maxHeight: 220, overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr style={{ background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)' }}>
                    <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '22%' }}>Item</th>
                    <th style={{ padding: '8px 4px', textAlign: 'left', fontWeight: 600, color: '#fff', width: '10%' }}>HSN</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>GST%</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Qty</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '10%' }}>Rate</th>
                    <th style={{ padding: '8px 4px', textAlign: 'center', fontWeight: 600, color: '#fff', width: '8%' }}>Disc%</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%' }}>Taxable</th>
                    <th style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, color: '#fff', width: '12%' }}>Total</th>
                    {!isViewMode && <th style={{ padding: '8px 4px', width: '5%', background: 'linear-gradient(135deg, #324372 0%, #2a3a63 100%)' }}></th>}
                  </tr>
                </thead>
                <tbody>
                  {SaleOrderDetails.map((detail, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                      <td style={{ padding: '4px' }}>
                        <FormControl fullWidth size="small">
                          <Select value={detail.ItemId || ''} onChange={(e) => handleItemSelect(index, e.target.value)} 
                            disabled={isViewMode} displayEmpty sx={{ fontSize: '0.7rem', '& .MuiSelect-select': { py: 0.5 } }}
                            MenuProps={{ PaperProps: { sx: { maxHeight: 200 } } }}>
                            <MenuItem value="" disabled sx={{ fontSize: '0.7rem' }}>Select</MenuItem>
                            {items.map(item => <MenuItem key={item.Id} value={item.Id} sx={{ fontSize: '0.7rem', py: 0.25 }}>{item.Name}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" value={detail.HSNCode || ''} onChange={(e) => handleDetailChange(index, 'HSNCode', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInputCenter, width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.GSTRate || ''} onChange={(e) => handleDetailChange(index, 'GSTRate', e.target.value)} 
                          disabled sx={{ ...tinyInputCenter, width: '100%', ...disabledFieldStyle }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.Quantity || ''} onChange={(e) => handleDetailChange(index, 'Quantity', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInputCenter, width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.Rate || ''} onChange={(e) => handleDetailChange(index, 'Rate', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInput, width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px' }}>
                        <TextField size="small" type="number" value={detail.DiscountPercentage || ''} onChange={(e) => handleDetailChange(index, 'DiscountPercentage', e.target.value)} 
                          disabled={isViewMode} sx={{ ...tinyInputCenter, width: '100%' }} />
                      </td>
                      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(detail.TaxableAmount)}</td>
                      <td style={{ padding: '4px', textAlign: 'right', fontWeight: 700, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount)}</td>
                      {!isViewMode && (
                        <td style={{ padding: '4px' }}>
                          <IconButton size="small" onClick={() => removeDetailRow(index)} sx={{ p: 0.25, color: '#ef4444' }}>
                            <Delete sx={{ fontSize: 14 }} />
                          </IconButton>
                        </td>
                      )}
                    </tr>
                  ))}
                  {SaleOrderDetails.length === 0 && (
                    <tr><td colSpan={isViewMode ? 8 : 9} style={{ textAlign: 'center', padding: '16px', color: '#6b7280' }}>No items added</td></tr>
                  )}
                </tbody>
              </table>
            </Box>
          </Box>
        )}

          <Divider sx={{ my: 1 }} />

          <Grid container spacing={isMobile ? 1 : 2}>
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>Additional Information</Typography>
              <Grid container spacing={0.75}>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="Freight" value={formData.FreightAmount || ''} 
                    onChange={(e) => handleChange('FreightAmount', e.target.value)} disabled={isViewMode} sx={compactInputRight} />
                </Grid>              
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="number" label="Packing" value={formData.PackingAmount || ''} 
                    onChange={(e) => handleChange('PackingAmount', e.target.value)} disabled={isViewMode} sx={compactInputRight} />
                </Grid>              
                <Grid item xs={12}>
                  <TextField fullWidth size="small" label="Remarks" value={formData.Remarks || ''} 
                    onChange={(e) => handleChange('Remarks', e.target.value)} disabled={isViewMode} 
                    inputProps={{ maxLength: 100 }}
                    multiline rows={1}
                    sx={compactInput} />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#1e40af', mb: 0.5, display: 'block' }}>Sale Order Summary</Typography>
              <Box sx={{ bgcolor: '#f8fafc', borderRadius: 1, p: 1 }}>
                {[
                  { label: 'Sub Total', value: formData.Amount },
                  { label: 'Discount', value: formData.DiscountAmount, isNeg: true },
                  { label: 'Taxable', value: formData.TaxableAmount },
                  ...(isIGSTInvoice() ? [
                    { label: 'IGST', value: formData.IGSTAmount }
                  ] : [
                    { label: 'CGST', value: formData.CGSTAmount },
                    { label: 'SGST', value: formData.SGSTAmount }
                  ]),
                  { label: 'Freight', value: parseFloat(formData.FreightAmount) || 0 },
                  { label: `Freight GST (${formData.FreightGSTRate || 0}%)`, value: parseFloat(formData.FreightGSTAmount) || 0 },
                  { label: 'Packing', value: parseFloat(formData.PackingAmount) || 0 },
                  { label: `Packing GST (${formData.PackingGSTRate || 0}%)`, value: parseFloat(formData.PackingGSTAmount) || 0 },
                  { label: 'Round Off', value: formData.RoundOff },
                ].map((row, i) => (
                  <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">{row.label}:</Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: row.isNeg ? '#dc2626' : 'inherit' }}>
                      {row.isNeg ? '- ' : ''}{formatCurrency(row.value)}
                    </Typography>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#1e40af' }}>Grand Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: '#1e40af', fontSize: '0.9rem' }}>{formatCurrency(formData.TotalAmount)}</Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', gap: 1 }}>
          <Button 
            onClick={handleCloseClick} 
            variant="outlined" 
            size="small" 
            sx={{ 
              borderColor: '#d1d5db', 
              color: '#374151', 
              fontSize: '0.8rem',
              px: 2,
              py: 0.75,
              textTransform: 'none',
              '&:hover': { borderColor: '#9ca3af', bgcolor: '#f3f4f6' }
            }}
          >
            {isViewMode ? 'Close' : 'Cancel'}
          </Button>
          
          {/* Show Verify button in VIEW mode OR in EDIT mode with no changes, when order is not yet verified */}
          {formData.Id && !formData.IsVerified && (modalAction === 'view' || (modalAction === 'edit' && !hasUnsavedChanges)) && (
            <Button 
              onClick={handleVerifyClick} 
              variant="contained" 
              size="small" 
              startIcon={<CheckCircle />}
              sx={{ 
                background: 'linear-gradient(135deg, #16a34a, #15803d)', 
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #15803d, #166534)',
                  boxShadow: '0 4px 8px rgba(22, 163, 74, 0.4)'
                }
              }}
            >
              Verify Sale Order
            </Button>
          )}

          {/* Show Update button when creating OR when editing with changes */}
          {!isViewMode && (modalAction === 'create' || hasUnsavedChanges) && (
            <Button 
              onClick={handleSave} 
              variant="contained" 
              size="small" 
              disabled={loading || !formData.MasterId || SaleOrderDetails.length === 0}
              sx={{ 
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)', 
                color: '#fff',
                fontSize: '0.8rem',
                px: 2,
                py: 0.75,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                '&:hover': { 
                  background: 'linear-gradient(135deg, #2563eb, #1e40af)',
                  boxShadow: '0 4px 8px rgba(59, 130, 246, 0.4)'
                },
                '&:disabled': {
                  background: '#e5e7eb',
                  color: '#9ca3af'
                }
              }}
            >
              {modalAction === 'create' ? 'Create Sale Order' : 'Update Sale Order'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog
          open={cancelConfirmOpen}
          onClose={handleStayOnPage}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                backgroundColor: '#fef3c7', 
                borderRadius: '50%', 
                width: 40, 
                height: 40, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Warning sx={{ color: '#f59e0b', fontSize: 24 }} />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
                Unsaved Changes
              </Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
              You have unsaved changes to the Sale order that will be lost if you leave this page.
            </Typography>
            <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
              Are you sure you want to exit without saving your changes?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={handleStayOnPage}
              variant="outlined"
              sx={{ 
                borderColor: '#1e40af', 
                color: '#1e40af', 
                '&:hover': { borderColor: '#1d4ed8', backgroundColor: '#dbeafe' } 
              }}
            >
              Stay on Page
            </Button>
            <Button
              onClick={handleConfirmCancel}
              variant="contained"
              sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}
            >
              Exit Without Saving
            </Button>
          </DialogActions>
        </Dialog>

      {/* Verify Confirmation Modal */}
      <Dialog
        open={verifyConfirmOpen}
        onClose={handleVerifyCancel}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ 
              backgroundColor: '#dcfce7', 
              borderRadius: '50%', 
              width: 40, 
              height: 40, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center' 
            }}>
              <CheckCircle sx={{ color: '#16a34a', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Verify Sale Order
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to verify Sale order <strong style={{ color: '#1e293b' }}>{formData.SaleOrderNo}</strong>?
          </Typography>
          <Typography sx={{ color: '#16a34a', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>
            Once verified, this Sale order will be confirmed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleVerifyCancel}
            variant="outlined"
            sx={{ 
              borderColor: '#e2e8f0', 
              color: '#64748b', 
              '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } 
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVerifyConfirm}
            variant="contained"
            sx={{ 
              backgroundColor: '#16a34a', 
              '&:hover': { backgroundColor: '#15803d' } 
            }}
          >
            Verify Sale Order
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SaleOrderModal;